/**
 * Authentication Service
 * Handles JWT token management, validation, storage, and refresh
 */

const TOKEN_KEY = "token";
const TOKEN_EXPIRY_BUFFER = 5 * 60 * 1000; // 5 minutes before expiry

/**
 * Decode JWT token without verification (client-side only)
 * Note: This is for reading token data, not for security validation
 */
const decodeToken = (token) => {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

/**
 * Get stored token from sessionStorage or localStorage
 */
export const getToken = () => {
  return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
};

/**
 * Store token in sessionStorage or localStorage
 */
export const setToken = (token, rememberMe = false) => {
  if (rememberMe) {
    localStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(TOKEN_KEY, token); // Also store in session for immediate access
  } else {
    sessionStorage.setItem(TOKEN_KEY, token);
    localStorage.removeItem(TOKEN_KEY); // Remove from localStorage if exists
  }
};

/**
 * Remove token from storage
 */
export const removeToken = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
};

/**
 * Check if token is expired or about to expire
 */
export const isTokenExpired = (token = null) => {
  const tokenToCheck = token || getToken();
  if (!tokenToCheck) return true;

  const decoded = decodeToken(tokenToCheck);
  if (!decoded || !decoded.exp) return true;

  // Check if token is expired or will expire within buffer time
  const expiryTime = decoded.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const timeUntilExpiry = expiryTime - currentTime;

  return timeUntilExpiry <= TOKEN_EXPIRY_BUFFER;
};

/**
 * Get token expiry time in milliseconds
 */
export const getTokenExpiryTime = (token = null) => {
  const tokenToCheck = token || getToken();
  if (!tokenToCheck) return null;

  const decoded = decodeToken(tokenToCheck);
  if (!decoded || !decoded.exp) return null;

  return decoded.exp * 1000; // Convert to milliseconds
};

/**
 * Get time until token expires in milliseconds
 */
export const getTimeUntilExpiry = (token = null) => {
  const expiryTime = getTokenExpiryTime(token);
  if (!expiryTime) return null;

  const currentTime = Date.now();
  return Math.max(0, expiryTime - currentTime);
};

/**
 * Check if token is valid (exists and not expired)
 */
export const isTokenValid = (token = null) => {
  const tokenToCheck = token || getToken();
  if (!tokenToCheck) return false;
  return !isTokenExpired(tokenToCheck);
};

/**
 * Get user information from token
 */
export const getUserFromToken = (token = null) => {
  const tokenToCheck = token || getToken();
  if (!tokenToCheck) return null;

  const decoded = decodeToken(tokenToCheck);
  if (!decoded) return null;

  return {
    id: decoded.sub,
    email: decoded.email,
    role: decoded.role,
    // Multi-store / sub-user claims (present on newer tokens).
    actor: decoded.actor || (decoded.role === "admin" ? "admin" : "vendor"),
    vendor_id: decoded.vendor_id,
    store_id: decoded.store_id,
  };
};

/**
 * Clear all authentication data
 */
export const clearAuthData = () => {
  removeToken();
  sessionStorage.removeItem("user_id");
  sessionStorage.removeItem("vendor_id");
  sessionStorage.removeItem("user_full_name");
  sessionStorage.removeItem("user_email");
  sessionStorage.removeItem("user_role");
  sessionStorage.removeItem("is_business_details");
  sessionStorage.removeItem("is_upload_chat");
  // Multi-store / sub-user session keys.
  sessionStorage.removeItem("actor");
  sessionStorage.removeItem("store_id");
  sessionStorage.removeItem("active_store_id");
  // Keep rememberMe email in localStorage
};

/**
 * Store user data after successful login
 */
export const storeUserData = (userData, token, rememberMe = false) => {
  setToken(token, rememberMe);
  
  if (userData.id) {
    sessionStorage.setItem("user_id", userData.id);
    if (userData.user_role?.toLowerCase() === "vendor") {
      sessionStorage.setItem("vendor_id", userData.id);
    }
  }
  
  if (userData.full_name) {
    sessionStorage.setItem("user_full_name", userData.full_name);
  }
  
  if (userData.email) {
    sessionStorage.setItem("user_email", userData.email);
  }
  
  if (userData.user_role) {
    sessionStorage.setItem("user_role", userData.user_role.toLowerCase());
  }
  
  if (userData.hasOwnProperty("is_business_details")) {
    sessionStorage.setItem("is_business_details", userData.is_business_details);
  }
  
  if (userData.hasOwnProperty("is_upload_chat")) {
    sessionStorage.setItem("is_upload_chat", userData.is_upload_chat);
  }
};

/**
 * Sign the browser in with a token from the backend (password sign-in, or
 * the signup email verification, which signs a new vendor straight in).
 * Writes the same session keys the Login page does.
 */
export const startSession = (token, rememberMe = false) => {
  const claims = getUserFromToken(token);
  if (!claims || !claims.id) return null;
  setToken(token, rememberMe);
  const role = (claims.role || "vendor").toLowerCase();
  sessionStorage.setItem("user_id", claims.id);
  const tenantId = claims.vendor_id != null ? claims.vendor_id : claims.id;
  if (role === "vendor" || claims.actor === "vendor_user") {
    sessionStorage.setItem("vendor_id", tenantId);
  }
  sessionStorage.setItem("actor", claims.actor || role);
  if (claims.store_id != null) {
    sessionStorage.setItem("store_id", String(claims.store_id));
  }
  if (claims.email) sessionStorage.setItem("user_email", claims.email);
  sessionStorage.setItem("user_role", role);
  return claims;
};

/**
 * Where a backend `next_step` sends a freshly signed-in vendor.
 */
export const pathForNextStep = (nextStep) => {
  if (nextStep === "onboarding") return "/onboarding";
  if (nextStep === "payment") return "/billing/payment-required";
  return null;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return isTokenValid();
};

const authService = {
  getToken,
  setToken,
  removeToken,
  isTokenExpired,
  isTokenValid,
  getTokenExpiryTime,
  getTimeUntilExpiry,
  getUserFromToken,
  clearAuthData,
  storeUserData,
  startSession,
  pathForNextStep,
  isAuthenticated,
  decodeToken,
};

export default authService;

